import type { LinkingRewardAssignment } from './linkingReward.service';

export interface LinkingRewardArm {
	variationId: number;
	rewardAmount: number;
}

export interface LinkingRewardState {
	/** Monto a prometer. 0 = control (o nada que prometer). */
	rewardAmount: number;
	/** Si el banner debe pintarse. */
	shouldPromise: boolean;
}

export const NO_REWARD: LinkingRewardState = {
	rewardAmount: 0,
	shouldPromise: false,
};

/**
 * Las CINCO condiciones que tienen que cumplirse ANTES de evaluar el
 * experimento. Es pura para poder probarla: el hook que la usa no se puede
 * testear en este repo (los tests corren en node y no hay testing-library), y
 * ésta es justo la lógica donde ya se nos escaparon dos bugs.
 *
 * Cada condición existe por una razón distinta, y confundirlas cuesta caro:
 *
 *	featuresReady    sin features cargadas, evalFeature devuelve el default y el
 *	                 usuario no cae en el experimento.
 *	growthbook       sin instancia no hay nada que evaluar.
 *	heruCashEnabled  fuera de la cohorte de heru-cash el usuario no puede ver ni
 *	                 gastar el crédito; prometerlo envenenaría el revenue neto,
 *	                 que es la métrica primaria.
 *	alreadyLinked    ver abajo.
 *	hashAttribute    `user_id` es el atributo de hasheo. Sin él GrowthBook
 *	                 descarta al usuario del experimento y devuelve el default
 *	                 SIN experimentResult — o sea, ni promesa ni exposición, y
 *	                 sin ninguna señal de que se escapó.
 */
export const canAssign = (deps: {
	featuresReady: boolean;
	hasGrowthbook: boolean;
	heruCashEnabled: boolean;
	hashAttribute: unknown;
	/**
	 * Si el usuario YA está vinculado no hay promesa que hacer: el otorgamiento
	 * exige `IsNewLink=true`, así que agregar un método sobre una cuenta ya
	 * ligada —o renovar la e.firma— nunca paga.
	 *
	 * El gate vive AQUÍ y no en cada pantalla porque las superficies de
	 * vinculación son MIXTAS: el mismo modal de e.firma lo abre un usuario nuevo
	 * en el onboarding de Zubale (vinculación real, el premio aplica) y uno ya
	 * vinculado renovando su credencial (no aplica). Sin este gate, cada montaje
	 * nuevo dependía de que su host hiciera el early-return correcto — algo que
	 * hoy pasa por accidente en unos (LinkingFlow, SatLinkingIntro) y no pasa en
	 * otros.
	 *
	 * Y lo que se evita no es sólo un banner de más: `evalFeature` dispara la
	 * EXPOSICIÓN, así que prometerle a un vinculado lo mete al denominador de su
	 * brazo sin que pueda convertir, diluyendo el efecto que se está midiendo.
	 */
	alreadyLinked: boolean;
}): boolean =>
	deps.featuresReady &&
	deps.hasGrowthbook &&
	deps.heruCashEnabled &&
	!deps.alreadyLinked &&
	deps.hashAttribute !== undefined &&
	deps.hashAttribute !== null &&
	deps.hashAttribute !== '';

/**
 * Traduce la evaluación de GrowthBook al brazo que se le manda al backend, o
 * `null` si no hay brazo que reportar.
 *
 * `experimentResult` sólo viene cuando el usuario CAYÓ en el experimento (el
 * SDK lo adjunta únicamente si `inExperiment && !passthrough`). Sin él, el
 * valor que traiga la feature es un default o un forzado, no una asignación:
 * mandarlo inventaría una exposición que no existió y contaminaría el análisis
 * con usuarios que nunca estuvieron en la prueba.
 *
 * El monto tiene que ser UN NÚMERO, no algo coercible a número: un dedazo en
 * GrowthBook —que es UI, no código revisado— llega hasta aquí sin pasar por un
 * PR. Ojo con el atajo `Number(value ?? 0)`: convierte una variación sin valor
 * en un 0, o sea que una variación rota se leería como CONTROL y se reportaría
 * como tal, metiendo al usuario en el brazo equivocado del análisis. El backend
 * rechazaría el par (variación 1 con monto 0), pero para entonces el daño ya
 * sería un usuario sin promesa y sin explicación.
 */
export const resolveArm = (evaluation?: {
	value?: unknown;
	experimentResult?: { variationId?: number };
}): LinkingRewardArm | null => {
	const variationId = evaluation?.experimentResult?.variationId;
	if (typeof variationId !== 'number') return null;

	const rewardAmount = evaluation?.value;
	if (typeof rewardAmount !== 'number') return null;
	if (!Number.isFinite(rewardAmount) || rewardAmount < 0) return null;

	return { variationId, rewardAmount };
};

/**
 * Traduce la respuesta del backend a lo que se pinta.
 *
 * DOS "CEROS" QUE NO SON EL MISMO CERO:
 *
 *	eligible: false        el kill switch del backend. El feature está apagado
 *	                       en este ambiente; el usuario no está en el
 *	                       experimento y no cuenta en ningún denominador.
 *	rewardAmount: 0        el brazo de CONTROL. El usuario SÍ está en el
 *	                       experimento y SÍ cuenta — sólo que su tratamiento es
 *	                       no recibir nada.
 *
 * Los dos terminan sin banner, y por eso es fácil colapsarlos por accidente.
 * Se distinguen aquí porque quien lee esta función después va a querer saber
 * cuál de los dos casos está viendo.
 *
 * El monto es SIEMPRE el que devuelve el backend, nunca el local: la primera
 * asignación congela el brazo del usuario, así que si el experimento se
 * recalibra a media prueba, GrowthBook puede devolver otro monto para alguien
 * que ya tenía promesa. Pintar el local le enseñaría $50 y le pagaría $100.
 */
export const toRewardState = (
	assignment?: LinkingRewardAssignment | null,
): LinkingRewardState => {
	if (!assignment?.eligible) return NO_REWARD;

	const rewardAmount = Number(assignment.rewardAmount ?? 0);
	if (!Number.isFinite(rewardAmount) || rewardAmount <= 0) return NO_REWARD;

	return { rewardAmount, shouldPromise: true };
};
